import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  Grid,
  Card,
  CardContent,
  Divider,
  AppBar,
  Toolbar,
  useTheme,
} from '@mui/material';
import {
  AccountCircle as AccountIcon,
  Code as CodeIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import Footer from '../components/Footer';
import Header from '../components/Header';

const HelpPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  
  const steps = [
    {
      label: 'Create Your Account',
      description: 'Sign up for a Glowspirit account and choose your subscription plan.',
    },
    {
      label: 'Configure Your Chatbot',
      description: 'Customize your chatbot\'s appearance, behavior, and responses to match your brand.',
    },
    {
      label: 'Add to Your Website',
      description: 'Copy the provided code snippet and paste it into your website\'s HTML.',
    },
  ];

  const codeSnippet = `<script>
  (function() {
      const config = {
          chatbotId: 'YOUR_CHATBOT_ID',  // Replace with your chatbot's unique ID
      };
  
      const script = document.createElement('script');
      script.src = \`https://widget.glowspirit.org/widget.js\`;
      script.async = true;
      script.onload = function() {
          if (window.Glowspirit) {
              window.Glowspirit.init(config);
          } else {
              console.error('Glowspirit script failed to load.');
          }
      };
      
      document.body.appendChild(script);
  })();
</script>`;

  return (
    <Box sx={{ 
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: theme.palette.mode === 'dark' ? 'background.default' : '#f8f9fa'
    }}>
      {/* Header */}
      <Header />

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ flex: 1, py: 4 }}>
        <Typography 
          variant="h3" 
          component="h1" 
          gutterBottom 
          align="center" 
          sx={{ 
            mb: 6, 
            fontWeight: 600, 
            color: theme.palette.mode === 'dark' ? 'primary.main' : '#1a237e' 
          }}
        >
          Getting Started with Glowspirit
        </Typography>

        {/* Implementation Steps */}
        <Paper 
          sx={{ 
            p: 4, 
            mb: 6, 
            borderRadius: 2, 
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'white',
          }}
        >
          <Typography 
            variant="h5" 
            gutterBottom 
            sx={{ 
              fontWeight: 600, 
              color: theme.palette.mode === 'dark' ? 'primary.main' : '#1a237e' 
            }}
          >
            Implementation Guide
          </Typography>
          <Stepper activeStep={-1} orientation="vertical">
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel>
                  <Typography variant="h6">{step.label}</Typography>
                </StepLabel>
                <StepContent>
                  <Typography>{step.description}</Typography>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </Paper>

        {/* Code Snippet */}
        <Paper 
          sx={{ 
            p: 4, 
            mb: 6, 
            borderRadius: 2, 
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'white',
          }}
        >
          <Typography 
            variant="h5" 
            gutterBottom 
            sx={{ 
              fontWeight: 600, 
              color: theme.palette.mode === 'dark' ? 'primary.main' : '#1a237e' 
            }}
          >
            Integration Code
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Add this code snippet to your website's HTML, just before the closing &lt;/body&gt; tag.
          </Typography>
          <Box
            sx={{
              bgcolor: 'grey.900',
              p: 3,
              borderRadius: 1,
              overflow: 'auto',
              fontFamily: 'monospace',
              color: 'grey.100',
            }}
          >
            <pre style={{ margin: 0 }}>{codeSnippet}</pre>
          </Box>
        </Paper>

        {/* Features Grid */}
        <Grid container spacing={4} sx={{ mb: 6 }}>
          <Grid item xs={12} md={4}>
            <Card 
              sx={{ 
                height: '100%', 
                borderRadius: 2, 
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'white',
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AccountIcon sx={{ fontSize: 40, color: theme.palette.mode === 'dark' ? 'primary.main' : '#1a237e', mr: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>Easy Setup</Typography>
                </Box>
                <Typography color="text.secondary">
                  Get started in minutes with our simple setup process and intuitive dashboard.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card 
              sx={{ 
                height: '100%', 
                borderRadius: 2, 
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'white',
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CodeIcon sx={{ fontSize: 40, color: theme.palette.mode === 'dark' ? 'primary.main' : '#1a237e', mr: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>Simple Integration</Typography>
                </Box>
                <Typography color="text.secondary">
                  One line of code to add your chatbot to any website or platform.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card 
              sx={{ 
                height: '100%', 
                borderRadius: 2, 
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'white',
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <SettingsIcon sx={{ fontSize: 40, color: theme.palette.mode === 'dark' ? 'primary.main' : '#1a237e', mr: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>Customizable</Typography>
                </Box>
                <Typography color="text.secondary">
                  Fully customize your chatbot's appearance and behavior to match your brand.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Support Section */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography 
            variant="h5" 
            gutterBottom 
            sx={{ 
              fontWeight: 600, 
              color: theme.palette.mode === 'dark' ? 'primary.main' : '#1a237e' 
            }}
          >
            Need Help?
          </Typography>
          <Typography color="text.secondary" paragraph>
            Our support team is here to help you get started and answer any questions.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={<CheckIcon />}
            onClick={() => navigate('/contact')}
            sx={{ 
              bgcolor: theme.palette.mode === 'dark' ? 'primary.dark' : '#1a237e',
              '&:hover': {
                bgcolor: theme.palette.mode === 'dark' ? 'primary.main' : '#000051'
              }
            }}
          >
            Contact Support
          </Button>
        </Box>
      </Container>

      {/* Footer */}
      <Footer />
    </Box>
  );
};

export default HelpPage; 